import { AfterViewInit, Component, DOCUMENT, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { CreateRolePayload, PermissionRow } from '../../../modules/shared/models/roles.modal';
import { CommonModule } from '@angular/common';
import { PermissionMatrixComponent } from '../permission-matrix/permission-matrix.component';
import { GenericResponse, PermissionDetails, RoleDetails, RoleResponse } from '../../../modules/shared/models/general.modal';
import alertify from 'alertifyjs';
import { HttpErrorResponse } from '@angular/common/http';
import { CharLength } from '../../../modules/directives/char-length.directive';

@Component({
  selector: 'app-role-creation-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PermissionMatrixComponent, CharLength],
  templateUrl: './role-creation-modal.component.html',
  styleUrl: './role-creation-modal.component.scss'
})
export class RoleCreationModalComponent  implements OnInit, AfterViewInit, OnDestroy{
  private readonly hostElement: HTMLElement;
    @Input() isOpen = false;
    @Output() closed = new EventEmitter<void>();
    @Output() roleSaved = new EventEmitter<any>();

  isLoadingRoleData: boolean = false;
  @Input() mode: "CREATE" | "UPDATE" | "VIEW" = "CREATE";
  @Input() roleId: string;

  form!: FormGroup;
  permissionRows: PermissionRow[] = [
    { module: 'Workflow Management', permissions: { view: false, create: false, edit: false, approve: false, delete: false } },
    { module: 'Vendor Management', permissions: { view: false, create: false, edit: false, approve: false, delete: false } },
    { module: 'Payments Management', permissions: { view: false, create: false, edit: false, approve: false, delete: false } },
    { module: 'Reports & Analytics', permissions: { view: false, create: false, edit: false, approve: false, delete: false } },
    { module: 'User Management', permissions: { view: false, create: false, edit: false, approve: false, delete: false } },
    { module: 'Role Management', permissions: { view: false, create: false, edit: false, approve: false, delete: false } }
  ];
   constructor(
    private fb: FormBuilder,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    private apiService:ApiServiceService,
  ) {
    this.hostElement = this.elementRef.nativeElement;

    this.form = this.fb.group({
      roleName: ['', Validators.required],
      status: ['Active', Validators.required],
      description: [''],
    });
  }

  ngOnInit(): void {
    console.log(this.mode)
    if (this.mode === "UPDATE" || this.mode === "VIEW") {
      if (this.mode === "VIEW") this.form.disable();
      this.loadRoleInfo();
    }
  };

  loadRoleInfo() {
    this.isLoadingRoleData = true;
    this.apiService.get(`/roles/${this.roleId}`).subscribe({
      next: (response: GenericResponse<RoleDetails>) => {
        if (response.status !== "success") {
          alertify.error(response.message || "Something went wrong while fetching roles");
          return;
        }
        // this.roleData = response.data;
        this.form.patchValue(
          {
            roleName: response.data.name,
            status: response.data.status,
            description: response.data.description,
          }
        )
        this.parsePermissionRows(response.data.permissions);
        this.isLoadingRoleData = false;
      },
      error: (error) => {
        if (error instanceof HttpErrorResponse) {
          alertify.error(error.message || "Failed to load role information")
        } else {
          alertify.error(error || "Failed to load role information");
        }
      }
    })
  }

  parsePermissionRows(permissions: PermissionDetails[]) {
    const gridData = this.mapPermissionsToGrid(permissions);
    this.permissionRows = gridData;
  }

  mapPermissionsToGrid(permissionsFromApi: any[]) {
    // 1. Extract just the 'name' strings from the objects
    const flatPermissionNames = permissionsFromApi.map(p => p.name);

    // 2. Define your system modules (the "pretty" names)
    const modules = [
      "Workflow Management", "Vendor Management", "Payments Management", "Reports & Analytics", "User Management", "Role Management"
    ];

    // 3. Reconstruct the grid
    const permissionRows = modules.map(moduleTitle => {
      // Sanitize title to match the "CAN_..._Module_Name" format
      // Note: Use /[^a-zA-Z0-9]/g to match the "Reports___Analytics" triple underscore
      const sanitizedModule = moduleTitle.replace(/[^a-zA-Z0-9]/g, '_');

      return {
        module: moduleTitle,
        permissions: {
          view: flatPermissionNames.includes(`CAN_VIEW_${sanitizedModule}`),
          create: flatPermissionNames.includes(`CAN_CREATE_${sanitizedModule}`),
          edit: flatPermissionNames.includes(`CAN_EDIT_${sanitizedModule}`),
          approve: flatPermissionNames.includes(`CAN_APPROVE_${sanitizedModule}`),
          delete: flatPermissionNames.includes(`CAN_DELETE_${sanitizedModule}`)
        }
      };
    });
    return permissionRows;
  }
  
    ngAfterViewInit(): void {
    this.renderer.appendChild(this.document.body, this.hostElement);
  }

  ngOnDestroy(): void {
    if (this.hostElement.parentNode === this.document.body) {
      this.renderer.removeChild(this.document.body, this.hostElement);
    }
  }

  onRowsChange(rows: PermissionRow[]): void {
    this.permissionRows = rows;
  }

  onClose(): void {
    this.closed.emit();
    this.resetForms();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.roleSaved.emit({
      roleName: this.form.value.roleName || '',
      status: this.form.value.status || 'Active',
      description: this.form.value.description || '',
      permissionRows: this.permissionRows,
    });
  }

    private resetForms(): void {
    this.form?.reset();
  }
}
